import { useState, useEffect } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Save, ChevronRight, ChevronLeft } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";

import { useStoreIndent, useIssueStoreIndent, useStoreLookups } from "@/features/store-indents/api";
import { useAdminUsers } from "@/features/users/api";
import { useAsset, useUpdateAsset, useLookups } from "@/features/assets/api";
import { useAssetProcurementLink } from "@/features/assets/generateApi";
import { CALIBRATION_INTERVAL_UNITS } from "@/features/assets/types";

export const Route = createFileRoute("/_authenticated/store-indents/$id/allocate/$itemId")({
  component: AllocateWizard,
});

const allocateSchema = z.object({
  // Commercial
  po_number: z.string().max(100).optional().or(z.literal("")),
  invoice_number: z.string().max(100).optional().or(z.literal("")),
  invoice_date: z.string().optional().or(z.literal("")),
  // Calibration
  calibration_required: z.boolean().optional().default(false),
  calibration_interval_value: z.preprocess(
    (v) => (v === "" || v === null || (typeof v === "number" && Number.isNaN(v)) ? undefined : v),
    z.coerce.number().int().min(1).optional(),
  ),
  calibration_interval_unit: z.enum(CALIBRATION_INTERVAL_UNITS).optional().or(z.literal("")),
  last_calibration_date: z.string().optional().or(z.literal("")),
  calibration_agency: z.string().max(200).optional().or(z.literal("")),
  calibration_certificate_required: z.boolean().optional().default(false),
  calibration_reminder_days: z.preprocess(
    (v) => (v === "" || v === null || (typeof v === "number" && Number.isNaN(v)) ? undefined : v),
    z.coerce.number().int().min(0).max(365).optional(),
  ),
  // Allocation Assignment
  assignment_type: z.enum(["department", "internal_user", "third_party"]),
  department_id: z.string().uuid("Select a department").optional().or(z.literal("")),
  allocated_to_id: z.string().uuid("Select a user").optional().or(z.literal("")),
  third_party_name: z.string().max(200).optional().or(z.literal("")),
  third_party_type: z.string().max(100).optional().or(z.literal("")),
  contact_person: z.string().max(200).optional().or(z.literal("")),
  phone: z.string().max(50).optional().or(z.literal("")),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  address: z.string().max(500).optional().or(z.literal("")),
  allocated_at: z.string().optional().or(z.literal("")),
  item_description: z.string().optional().or(z.literal("")),
}).superRefine((v, ctx) => {
  if (!v.allocated_at) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["allocated_at"], message: "Allocation date is required" });
  }
  if (v.assignment_type === "department" && !v.department_id) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["department_id"], message: "Department is required" });
  }
  if (v.assignment_type === "internal_user" && !v.allocated_to_id) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["allocated_to_id"], message: "User is required" });
  }
  if (v.assignment_type === "third_party" && !v.third_party_name?.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["third_party_name"], message: "Third party name is required" });
  }
});

type FormValues = z.infer<typeof allocateSchema>;

function Field({ label, error, required, children }: { label: string; error?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}{required && <span className="text-destructive"> *</span>}</Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

const STEPS = ["Commercial", "Technical", "Allocation Target"];

function AllocateWizard() {
  const { id: storeIndentId, itemId: storeIndentItemId } = Route.useParams();
  const navigate = useNavigate();

  const { data: indent, isLoading: indentLoading } = useStoreIndent(storeIndentId);
  const indentItem = indent?.items?.find((it: any) => it.id === storeIndentItemId);
  const assetId = indentItem?.asset?.id;
  const { data: asset, isLoading: assetLoading } = useAsset(assetId);
  const { data: procLink, isLoading: linkLoading } = useAssetProcurementLink(assetId);
  
  const { data: lookups } = useStoreLookups();
  const { data: users } = useAdminUsers();
  
  const issueMut = useIssueStoreIndent();
  const updateAsset = useUpdateAsset();

  const [step, setStep] = useState(0);

  const form = useForm<FormValues>({
    resolver: zodResolver(allocateSchema),
    defaultValues: {
      po_number: "",
      invoice_number: "",
      invoice_date: "",
      calibration_required: false,
      calibration_interval_value: undefined,
      calibration_interval_unit: "months",
      last_calibration_date: "",
      calibration_agency: "",
      calibration_certificate_required: false,
      calibration_reminder_days: undefined,
      assignment_type: "department",
      department_id: "",
      allocated_at: new Date().toISOString().split("T")[0],
      item_description: "",
    },
  });

  const { watch, setValue, register, formState: { errors }, reset, trigger } = form;
  const assignmentType = watch("assignment_type");
  const calRequired = watch("calibration_required");

  // Pre-fill form when data is loaded
  useEffect(() => {
    if (asset && indentItem) {
      reset({
        po_number: (asset as any).purchase_order_number || (asset as any).custom_fields?.po_number || procLink?.po?.po_number || "",
        invoice_number: (asset as any).custom_fields?.invoice_number || procLink?.invoice_number || "",
        invoice_date: (asset as any).custom_fields?.invoice_date || procLink?.invoice_date || "",
        calibration_required: !!((asset as any).requires_calibration || (asset.calibration_interval_value && asset.calibration_interval_unit)),
        calibration_interval_value: asset.calibration_interval_value || undefined,
        calibration_interval_unit: (asset.calibration_interval_unit as any) || "months",
        last_calibration_date: asset.last_calibration_date || "",
        calibration_agency: asset.calibration_agency || "",
        calibration_certificate_required: asset.calibration_certificate_required || false,
        calibration_reminder_days: asset.calibration_reminder_days || undefined,
        assignment_type: "department",
        department_id: indent?.department_id || "",
        allocated_at: new Date().toISOString().split("T")[0],
        item_description: (asset as any).item_description || "",
      });
    }
  }, [asset, procLink, indent, indentItem, reset]);

  if (indentLoading || assetLoading || linkLoading) {
    return <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  if (!indent || !indentItem || !asset) {
    return <div className="p-10 text-center text-destructive">Required data not found.</div>;
  }

  const branchId = indent.branch_id;
  const branchDepartments = lookups?.departments?.filter((d: any) => !branchId || d.branch_id === branchId) || [];
  const branchUsers = users?.filter((u: any) => !branchId || u.branch_id === branchId && u.is_active !== false) || [];

  const handleNext = async () => {
    // Validate current step
    let fieldsToValidate: (keyof FormValues)[] = [];
    if (step === 0) {
      fieldsToValidate = ["po_number", "invoice_number", "invoice_date"];
    } else if (step === 1) {
      fieldsToValidate = ["calibration_required", "calibration_interval_value", "calibration_interval_unit", "last_calibration_date", "calibration_agency", "calibration_certificate_required", "calibration_reminder_days"];
    }
    const isValid = await trigger(fieldsToValidate);
    if (isValid) setStep((s) => s + 1);
  };

  const onSubmit = async (values: FormValues) => {
    try {
      // 1. Update Asset
      await updateAsset.mutateAsync({
        id: asset.id,
        values: {
          po_number: values.po_number || undefined,
          invoice_number: values.invoice_number || undefined,
          invoice_date: values.invoice_date || undefined,
          item_description: values.item_description || undefined,
          calibration_interval_value: values.calibration_required ? values.calibration_interval_value : undefined,
          calibration_interval_unit: values.calibration_required ? values.calibration_interval_unit : undefined,
          last_calibration_date: values.calibration_required ? values.last_calibration_date : undefined,
          calibration_agency: values.calibration_required ? values.calibration_agency : undefined,
          calibration_certificate_required: values.calibration_required ? values.calibration_certificate_required : false,
          calibration_reminder_days: values.calibration_required ? values.calibration_reminder_days : undefined,
        }
      });

      // 2. Issue Store Indent
      await issueMut.mutateAsync({
        storeIndentId,
        lines: [{
          store_indent_item_id: storeIndentItemId,
          qty_issued: 1,
          assignment_type: values.assignment_type,
          department_id: values.department_id || undefined,
          allocated_to_id: values.allocated_to_id || undefined,
          third_party_name: values.third_party_name || undefined,
          third_party_type: values.third_party_type || undefined,
          contact_person: values.contact_person || undefined,
          phone: values.phone || undefined,
          email: values.email || undefined,
          address: values.address || undefined,
          allocated_at: values.allocated_at || undefined,
        }]
      });

      toast.success("Asset successfully allocated");
      navigate({ to: "/store-indents/$id", params: { id: storeIndentId } });
    } catch (e: unknown) {
      toast.error((e as Error).message || "Allocation failed");
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate({ to: "/store-indents/$id", params: { id: storeIndentId } })}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Allocate Asset</h1>
          <p className="text-sm text-muted-foreground">
            {asset.asset_code} · {asset.name}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="space-y-1">
            <CardTitle>Step {step + 1} of {STEPS.length}: {STEPS[step]}</CardTitle>
            <CardDescription>Review and complete asset details before allocation.</CardDescription>
          </div>
          <Progress value={((step + 1) / STEPS.length) * 100} className="mt-4" />
        </CardHeader>
        <CardContent>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              
              {step === 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-full mb-2 text-sm text-muted-foreground border-b pb-2">
                    Commercial details pre-filled from GRN if available.
                  </div>
                  <Field label="PO Number" error={errors.po_number?.message}>
                    <Input {...register("po_number")} />
                  </Field>
                  <Field label="Invoice Number" error={errors.invoice_number?.message}>
                    <Input {...register("invoice_number")} />
                  </Field>
                  <Field label="Invoice Date" error={errors.invoice_date?.message}>
                    <Input type="date" {...register("invoice_date")} max={new Date().toISOString().split("T")[0]} />
                  </Field>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="calib"
                      checked={calRequired}
                      onCheckedChange={(c) => setValue("calibration_required", !!c, { shouldValidate: true })}
                    />
                    <Label htmlFor="calib">Requires Calibration</Label>
                  </div>

                  {calRequired && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                      <Field label="Interval Value" error={errors.calibration_interval_value?.message}>
                        <Input type="number" min={1} {...register("calibration_interval_value")} />
                      </Field>
                      <Field label="Interval Unit" error={errors.calibration_interval_unit?.message}>
                        <Select
                          value={watch("calibration_interval_unit") || "months"}
                          onValueChange={(v) => setValue("calibration_interval_unit", v as any, { shouldValidate: true })}
                        >
                          <SelectTrigger><SelectValue placeholder="Unit" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="days">Days</SelectItem>
                            <SelectItem value="months">Months</SelectItem>
                            <SelectItem value="years">Years</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Last Calibration Date" error={errors.last_calibration_date?.message}>
                        <Input type="date" {...register("last_calibration_date")} max={new Date().toISOString().split("T")[0]} />
                      </Field>
                      <Field label="Calibration Agency" error={errors.calibration_agency?.message}>
                        <Input {...register("calibration_agency")} />
                      </Field>
                      <Field label="Reminder Days" error={errors.calibration_reminder_days?.message}>
                        <Input type="number" min={0} max={365} {...register("calibration_reminder_days")} placeholder="e.g. 15" />
                      </Field>
                      <div className="flex items-center space-x-2 mt-6">
                        <Checkbox
                          id="cert_req"
                          checked={watch("calibration_certificate_required")}
                          onCheckedChange={(c) => setValue("calibration_certificate_required", !!c, { shouldValidate: true })}
                        />
                        <Label htmlFor="cert_req">Certificate Required?</Label>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6">
                  <Field label="Allocation Date" required error={errors.allocated_at?.message}>
                    <Input type="date" {...register("allocated_at")} max={new Date().toISOString().split("T")[0]} />
                  </Field>

                  <div className="space-y-4 border rounded-md p-4 bg-muted/20">
                    <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Assignment Target</h5>
                    <RadioGroup
                      value={assignmentType}
                      onValueChange={(v: "department" | "internal_user" | "third_party") => {
                        setValue("assignment_type", v, { shouldValidate: true });
                        if (v !== "internal_user") setValue("allocated_to_id", "");
                        if (v !== "third_party") {
                          setValue("third_party_name", "");
                          setValue("third_party_type", "");
                        }
                      }}
                      className="flex items-center gap-4 flex-wrap"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="department" id="dept" />
                        <Label htmlFor="dept">Department</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="internal_user" id="user" />
                        <Label htmlFor="user">User</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="third_party" id="tp" />
                        <Label htmlFor="tp">Third Party</Label>
                      </div>
                    </RadioGroup>

                    <div className="grid grid-cols-1 gap-4 mt-4">
                      {assignmentType !== "third_party" && (
                        <Field label="Department" required error={errors.department_id?.message}>
                          <Select
                            value={watch("department_id") || "none"}
                            onValueChange={(v) => setValue("department_id", v === "none" ? "" : v, { shouldValidate: true })}
                          >
                            <SelectTrigger><SelectValue placeholder="Select Department" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">-- Select Department --</SelectItem>
                              {branchDepartments.map((d: any) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </Field>
                      )}

                      {assignmentType === "internal_user" && (
                        <Field label="User" required error={errors.allocated_to_id?.message}>
                          <Select
                            value={watch("allocated_to_id") || "none"}
                            onValueChange={(v) => setValue("allocated_to_id", v === "none" ? "" : v, { shouldValidate: true })}
                          >
                            <SelectTrigger><SelectValue placeholder="Select User" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">-- Select User --</SelectItem>
                              {branchUsers.map((u: any) => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </Field>
                      )}

                      {assignmentType === "third_party" && (
                        <>
                          <Field label="Third Party Name" required error={errors.third_party_name?.message}>
                            <Input {...register("third_party_name")} placeholder="Vendor / Agency Name" />
                          </Field>
                          <div className="grid grid-cols-2 gap-4">
                            <Field label="Type">
                              <Input {...register("third_party_type")} placeholder="e.g. Vendor" />
                            </Field>
                            <Field label="Contact Person">
                              <Input {...register("contact_person")} placeholder="Name" />
                            </Field>
                            <Field label="Phone">
                              <Input {...register("phone")} placeholder="Phone" />
                            </Field>
                            <Field label="Email" error={errors.email?.message}>
                              <Input {...register("email")} placeholder="Email" type="email" />
                            </Field>
                          </div>
                          <Field label="Address">
                            <Textarea {...register("address")} rows={2} placeholder="Full address" />
                          </Field>
                        </>
                      )}
                      
                      <Field label="Asset Description" error={errors.item_description?.message}>
                        <Textarea {...register("item_description")} rows={2} placeholder="Detailed description of the asset..." />
                      </Field>
                    </div>
                  </div>
                </div>
              )}

              <Separator />
              
              <div className="flex justify-between items-center">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => step > 0 ? setStep(step - 1) : navigate({ to: `/store-indents/${storeIndentId}` })}
                >
                  {step === 0 ? "Cancel" : <><ChevronLeft className="mr-2 h-4 w-4" /> Previous</>}
                </Button>

                {step < STEPS.length - 1 ? (
                  <Button type="button" onClick={handleNext}>
                    Next <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <Button type="submit" disabled={issueMut.isPending || updateAsset.isPending}>
                    {(issueMut.isPending || updateAsset.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <Save className="mr-2 h-4 w-4" />
                    Complete Allocation
                  </Button>
                )}
              </div>
            </form>
          </FormProvider>
        </CardContent>
      </Card>
    </div>
  );
}
